import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { addDays, getMonthData, getMonthName } from './dateUtils';

const DatePickerWrapper = styled('div')(({ theme }) => ({
  backgroundColor: '#fff',
  borderRadius: '8px',
  padding: '16px',
  width: 'fit-content',
  minWidth: '280px',
}));

const CalendarHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '16px',
});

const MonthYearDisplay = styled('div')({
  fontSize: '1rem',
  fontWeight: 500,
});

const WeekDaysContainer = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '4px',
  marginBottom: '8px',
});

const WeekDay = styled('div')({
  textAlign: 'center',
  fontSize: '0.75rem',
  color: '#666',
  padding: '4px',
});

const DaysGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '4px',
});

const DayButton = styled('button')(({ isSelected, isToday, isDisabled }) => ({
  width: '100%',
  aspectRatio: '1',
  border: 'none',
  borderRadius: '4px',
  backgroundColor: isSelected ? '#1976d2' : isToday ? '#e3f2fd' : 'transparent',
  color: isSelected ? '#fff' : isDisabled ? '#ccc' : '#000',
  cursor: isDisabled ? 'default' : 'pointer',
  '&:hover': {
    backgroundColor: isDisabled ? undefined : isSelected ? '#1565c0' : '#f5f5f5',
  },
  '&:focus': {
    outline: 'none',
    boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
  },
}));

const PeriodSelector = styled('div')({
  marginBottom: '16px',
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
});

const PeriodButton = styled('button')(({ isSelected }) => ({
  padding: '6px 12px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  backgroundColor: isSelected ? '#1976d2' : '#fff',
  color: isSelected ? '#fff' : '#000',
  fontSize: '0.875rem',
  cursor: 'pointer',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: isSelected ? '#1565c0' : '#f5f5f5',
    borderColor: isSelected ? '#1565c0' : '#bbb',
  },
  '&:focus': {
    outline: 'none',
    boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
  },
}));

const IconButton = styled('button')({
  background: 'none',
  border: 'none',
  padding: '8px',
  cursor: 'pointer',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#666',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  '&:focus': {
    outline: 'none',
    backgroundColor: '#f5f5f5',
  },
});

const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const Vencimento = ({ value, onChange, selectedPeriod, setSelectedPeriod }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  
  const handlePeriodChange = (days) => {
    const daysValue = String(days);
    setSelectedPeriod(daysValue);
    const baseDate = new Date();
    baseDate.setHours(0, 0, 0, 0);
    onChange(addDays(baseDate, days), true);
  };

  const handlePrevMonth = (e) => {
    e.stopPropagation();
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const handleNextMonth = (e) => {
    e.stopPropagation();
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const monthData = getMonthData(currentDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of day for consistent comparison

  const handleDateClick = (date) => {
    // Compare dates only (ignore time)
    const dateToCompare = new Date(date);
    dateToCompare.setHours(0, 0, 0, 0);
    if (dateToCompare < today) return;
    onChange(date, false);
  };

  return (
    <DatePickerWrapper onClick={(e) => e.stopPropagation()}>
      
      <PeriodSelector>
        <PeriodButton
          isSelected={selectedPeriod === '0'}
          onClick={() => handlePeriodChange(0)}
        >
          Pronto
        </PeriodButton>
        <PeriodButton
          isSelected={selectedPeriod === '7'}
          onClick={() => handlePeriodChange(7)}
        >
          7 dias
        </PeriodButton>
        <PeriodButton
          isSelected={selectedPeriod === '14'}
          onClick={() => handlePeriodChange(14)}
        >
          14 dias
        </PeriodButton>
        <PeriodButton
          isSelected={selectedPeriod === '30'}
          onClick={() => handlePeriodChange(30)}
        >
          30 dias
        </PeriodButton>
        <PeriodButton
          isSelected={selectedPeriod === '90'}
          onClick={() => handlePeriodChange(90)}
        >
          90 dias
        </PeriodButton>
      </PeriodSelector>

      <CalendarHeader>
        <IconButton onClick={handlePrevMonth}>
          <MdChevronLeft size={24} />
        </IconButton>
        <MonthYearDisplay>
          {getMonthName(currentDate)} {currentDate.getFullYear()}
        </MonthYearDisplay>
        <IconButton onClick={handleNextMonth}>
          <MdChevronRight size={24} />
        </IconButton>
      </CalendarHeader>

      <WeekDaysContainer>
        {weekDays.map(day => (
          <WeekDay key={day}>{day}</WeekDay>
        ))}
      </WeekDaysContainer>

      <DaysGrid>
        {monthData.map((date, index) => {
          if (!date) return <div key={index} />;
          
          const isSelected = value && 
            date.getDate() === value.getDate() &&
            date.getMonth() === value.getMonth() &&
            date.getFullYear() === value.getFullYear();
          
          const isToday = 
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();
          
          // Compare dates only (ignore time)
          const dateToCompare = new Date(date);
          dateToCompare.setHours(0, 0, 0, 0);
          const isDisabled = dateToCompare < today;

          return (
            <DayButton
              key={index}
              onClick={() => handleDateClick(date)}
              isSelected={isSelected}
              isToday={isToday}
              isDisabled={isDisabled}
            >
              {date.getDate()}
            </DayButton>
          );
        })}
      </DaysGrid>
    </DatePickerWrapper>
  );
};

export default Vencimento;